// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Currency } from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { getMinorUnitDigits } from '@/currency/utilities/validate';
import { Decimal } from 'decimal.js';

const WIRE_MINIMUM_DECIMALS = 2;

/** Request-body shape of a Currency, built only by the serializers below. */
interface WireCurrency {
  amount: string;
  isoCode: IsoCurrencyCode;
}

/**
 * Serializes a Currency for an API request body, mirroring the BE wire shape:
 * the amount is padded to at least 2 decimals and never truncated, so an
 * amount carrying more precision than the backend accepts is rejected there
 * rather than silently changed here.
 */
export function serializeCurrencyForAPI(value: Currency): WireCurrency {
  return {
    amount: value.amount.toFixed(
      Math.max(WIRE_MINIMUM_DECIMALS, value.amount.decimalPlaces())
    ),
    isoCode: value.isoCode,
  };
}

/**
 * Serializes an optional Currency for an API request body: the wire object, or
 * null when the field is absent (a cleared or blank money field).
 */
export function serializeCurrencyForAPINullable(
  value: Currency | null
): WireCurrency | null {
  return value ? serializeCurrencyForAPI(value) : null;
}

/**
 * Formats an amount as a plain decimal string padded to the currency's minor
 * unit, keeping any extra digits rather than rounding them away.
 */
export function padAmountToMinorUnit(
  amount: Decimal,
  currencyCode: IsoCurrencyCode
): string {
  return amount.toFixed(
    Math.max(getMinorUnitDigits(currencyCode), amount.decimalPlaces())
  );
}

/** Serializes a Currency for a partial update, where undefined leaves the field unchanged. */
export function serializeOptionalCurrencyForAPI(
  value: Currency | null | undefined
): WireCurrency | null | undefined {
  return value === undefined
    ? undefined
    : serializeCurrencyForAPINullable(value);
}
